import React, { useEffect, useMemo, useState } from 'react';
import {
  Link as RouterLink } from 'react-router-dom';
import { Center,
  Spinner,
  VStack,
  Text,
  Button,
} from '@chakra-ui/react';
import { avalesAPI, bandasAPI } from '../services/api';
import BandMicrosite from './BandMicrosite.jsx';
import { buildSections, prepareData } from './microsite/micrositeUtils';
import MicrositeModerno from './microsite/MicrositeModerno.jsx';
import MicrositeClasico from './microsite/MicrositeClasico.jsx';
import MicrositeElegante from './microsite/MicrositeElegante.jsx';
import MicrositeAurora from './microsite/MicrositeAurora.jsx';
import MicrositeEditorial from './microsite/MicrositeEditorial.jsx';
import MicrositePulso from './microsite/MicrositePulso.jsx';
import MicrositeVortex from './microsite/MicrositeVortex.jsx';
import MicrositeGlass from './microsite/MicrositeGlass.jsx';
import MicrositeKinetic from './microsite/MicrositeKinetic.jsx';

/**
 * Vista previa autenticada de la web pública de la banda (mismo contenido que el micrositio por slug).
 * Útil en localhost o cuando no hay subdominio configurado.
 */
export default function BandMicrositePreview() {
  const [slug, setSlug] = useState(null);
  const [bandaData, setBandaData] = useState(null);
  const [avales, setAvales] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setAvales([]);
        setStats(null);
        const res = await bandasAPI.getMyProfile();
        if (!cancelled) {
          setSlug(res.data?.slug || null);
        }
        // La vista previa debe mostrar el borrador (aunque el subdominio sirva solo publicado).
        const preview = await bandasAPI.getMicrositePreview();
        const data = preview.data || null;
        if (!cancelled) setBandaData(data);
        if (data?.id && !cancelled) {
          const [avalesRes, statsRes] = await Promise.all([
            avalesAPI.getPublicosBanda(data.id).catch(() => ({ data: [] })),
            bandasAPI.getStats(data.id).catch(() => ({ data: null })),
          ]);
          if (!cancelled) {
            setAvales(avalesRes.data || []);
            setStats(statsRes.data || null);
          }
        }
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const previewPrepared = useMemo(() => {
    if (!bandaData) return null;
    return prepareData(bandaData);
  }, [bandaData]);

  const previewSections = useMemo(() => {
    if (!bandaData || !previewPrepared) return [];
    return buildSections(
      bandaData,
      previewPrepared.msConfig || {},
      avales,
      previewPrepared.eventosPublicos || [],
      previewPrepared.ensayosPublicos || [],
    );
  }, [bandaData, previewPrepared, avales]);

  if (loading) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Spinner size="xl" color="purple.500" borderWidth="4px" />
      </Center>
    );
  }

  if (loadError) {
    return (
      <Center minH="100vh" bg="gray.50" p={6}>
        <VStack gap={4} maxW="md" textAlign="center">
          <Text fontSize="lg" color="gray.600">
            No se pudo cargar tu perfil. Vuelve a intentarlo más tarde.
          </Text>
          <Button colorPalette="purple" asChild><RouterLink to="/banda/perfil">Ir al perfil
                      </RouterLink></Button>
        </VStack>
      </Center>
    );
  }

  if (!slug) {
    return (
      <Center minH="100vh" bg="gray.50" p={6}>
        <VStack gap={4} maxW="md" textAlign="center">
          <Text fontSize="lg" color="gray.600">
            Aún no has activado la URL de tu web. Elige y guarda un nombre de subdominio en la pestaña «Tu Web» del perfil para poder previsualizarla.
          </Text>
          <Button colorPalette="purple" asChild><RouterLink to="/banda/perfil">Ir al perfil
                      </RouterLink></Button>
        </VStack>
      </Center>
    );
  }

  if (!bandaData) {
    return <BandMicrosite slug={slug} />;
  }

  const {
    msConfig,
    contacto,
    social,
    ensayosPublicos,
    eventosPublicos,
    actuacionesPublicas,
    locationText,
    hasSocials,
    primaryColor,
    secondaryColor,
    surfaceColor,
  } = previewPrepared;

  const commonProps = {
    bandaData,
    avales,
    stats,
    sections: previewSections,
    msConfig,
    contacto,
    social,
    ensayosPublicos,
    eventosPublicos,
    actuacionesPublicas,
    locationText,
    hasSocials,
    primaryColor,
    secondaryColor,
    surfaceColor,
  };

  const tema = msConfig?.tema || 'moderno';
  if (tema === 'clasico') return <MicrositeClasico {...commonProps} />;
  if (tema === 'elegante') return <MicrositeElegante {...commonProps} />;
  if (tema === 'aurora') return <MicrositeAurora {...commonProps} />;
  if (tema === 'editorial') return <MicrositeEditorial {...commonProps} />;
  if (tema === 'pulso') return <MicrositePulso {...commonProps} />;
  if (tema === 'vortex') return <MicrositeVortex {...commonProps} />;
  if (tema === 'glass') return <MicrositeGlass {...commonProps} />;
  if (tema === 'kinetic') return <MicrositeKinetic {...commonProps} />;
  return <MicrositeModerno {...commonProps} />;
}
